import Vue from 'vue';
import {getModule} from 'vuex-module-decorators';

import {Time} from '@/utils/Time';
import {EventBus} from '@/utils/EventBus';

import {Level} from '@/model/Notification';

import {INetworkNetworkingService} from '@/services/network/INetworkNetworkingService';
import {IApiService} from '@/services/IApi.service';

import {ApiStore} from '@/store/Api.store';
import NetworkSafeUpdateStore, {SaveState} from '@/store/network/NetworkSafeUpdate.store';

import {NetworkRestoreProcedure} from '@/components/Networking/NetworkRestoreProcedure';
import {ILogger} from '@/utils/ILogger';
import {LogdownLogger} from '@/utils/LogdownLogger';

export class ErrorsBag {
  constructor(public Main: Error,
              public Details: Error[]) {
  }
}

export class SafeOperation {

  private logger: ILogger = new LogdownLogger('SafeOperation');

  private store: NetworkSafeUpdateStore;

  private apiStore: ApiStore;

  private rollbackStabilisationTime = 30;

  constructor(
    private networkingService: INetworkNetworkingService,
    private apiService: IApiService,
  ) {
    this.store = getModule(NetworkSafeUpdateStore, Vue.prototype.$store);
    this.apiStore = getModule(ApiStore, Vue.prototype.$store);
  }

  /**
   * Executes an operation, restoring the previous networking state if connection is lost after the operation
   * @param operationProcedure A function containing the procedure after which the connection may be lost
   * @param preProcedure A function containing a procedure that will be made before the main operation. The effects of
   * this procedure also have to be reverted in case of connection loss. If this procedure fails, the operation is not
   * executed and the changes are reverted directly
   * @param notify A function to handle notification emitted by the safe update
   * @param newHost The new address where the device should be accessible. If undefined, the current address will be used.
   * @param timeout The time (in seconds) after which the rollback will occur if no connection has been recovered.
   * The time counts down from the start of the preProcedure.
   * @param devices List of devices concerned by the sensitive operation. If undefined, all devices configuration will
   * be rolled back
   */
  public async Execute(
    operationProcedure: () => Promise<void>,
    preProcedure?: () => Promise<void>,
    notify?: (level: Level, message: string, details?: string[], error?: Error) => void,
    newHost?: string,
    timeout: number = 30,
    devices?: string[],
    rollbackPostProcedure?: () => Promise<void>,
  ): Promise<void> {

    const originalRemote = this.apiStore.Remote;
    const originalSecured = this.apiStore.Secured;

    this.store.setHost(newHost ? newHost : originalRemote);
    this.store.setSaveState(SaveState.Initialize);

    try {
      /* 1. Set the checkpoint */
      this.logger.trace('Create checkpoint');
      const restoreProcedure = await this.CreateRestoreProcedure(this.networkingService, timeout, devices);

      /* 2. Update settings */
      if (preProcedure) {
        this.logger.trace('Pre-procedure is defined');
        try {
          this.logger.trace('Setting state to Save');
          this.store.setSaveState(SaveState.Save);
          await preProcedure();
          this.logger.trace('Pre-procedure is done');
        } catch (error) {
          await this.RemoveRestoreProcedure(restoreProcedure); // TODO: merge errors
          throw error;
        }
      }

      /* The new settings are saved, but not yet active */

      /* 3. Load new settings (connect) */
      try {
        this.logger.trace('Suspending API checks');
        this.apiStore.setCheckSuspended(true);

        this.logger.trace('Starting operation');
        await operationProcedure();
        this.logger.trace('Operation done successfully');

      } catch (error) {

        this.logger.debug('Operation failed', error);

        if (!error.Main) {
          error.Main = error;
        }

        if (!preProcedure) {
          this.logger.trace('No pre-procedure to revert, we are done');
          throw error;
        }

        if (notify) {
          notify(
            Level.Error,
            `${error.Main.message}<br>Already updated settings are now being reverted, please wait`,
            error.Details?.map((d: any) => d.message),
            error,
          );
        }
        this.store.setSaveState(SaveState.Restore);

        try {
          this.logger.trace('Force rollback');
          await restoreProcedure.rollback();
        } catch (error) {
          throw new Error(`Settings rollback has failed<br>${error.message}`); // TODO: warn state
        }

        /* We wait an extra second to be sure the device has time to rollback before we try to reconnect */
        await Time.sleep(1000);

        const rollbackEndDeadline = new Date();
        rollbackEndDeadline.setSeconds(rollbackEndDeadline.getSeconds() + (this.rollbackStabilisationTime * 1000));

        try {
          this.logger.trace('Waiting for rollback to end', rollbackEndDeadline);
          await this.apiService.waitConnected(rollbackEndDeadline);
          this.logger.trace('Rollback done, we are connected again');
        } catch (error) {
          throw new Error(`The connection could not be recovered: ${error.message}`);
        }

        if (notify) {
          notify(Level.Info, `The previous settings have been restored`);
        }

        throw new Error();
      }

      /* At this time, the gateway is applying the new settings, we may lose connection */
      this.logger.trace('New settings being applied, we may lose connection');

      /* 4. Wait for connect */
      try {
        this.store.setSaveState(SaveState.Connect);

        /* Update the remote with the new one */
        this.apiStore.setRemote(this.store.Host);
        this.apiService.configure(this.store.Host, originalSecured);

        this.logger.trace('Waiting for reconnect', restoreProcedure.timeout);
        await this.apiService.waitConnected(restoreProcedure.timeout);
        this.logger.trace('Connection recovered');

        /* At this time, connection with the new settings has been recovered */

        this.store.setSaveState(SaveState.Finalize);

        try {
          this.logger.trace('Cancelling the rollback procedure');
          await restoreProcedure.cancel();
          return;
        } catch (error) {
          this.logger.error('The rollback could not be cancelled');
          if (notify) {
            notify(Level.Error, `The restoration point could not be removed`, [`A revert will occur, please wait...`], error);
          }

          this.logger.trace('Wait for rollback');
          await Time.sleep(restoreProcedure.timeout.getSeconds() - Date.now());
        }
      } catch (error) {
        this.logger.info('Could not reconnect to the gateway, a revert will occur', error);
      }

      this.logger.trace('Rollback is occurring');
      /* At this time, the rollback checkpoint has been reached, and the rollback is starting */
      this.store.setSaveState(SaveState.Restore);

      const rollbackEndDeadline = new Date();
      rollbackEndDeadline.setSeconds(rollbackEndDeadline.getSeconds() + (this.rollbackStabilisationTime * 1000));

      this.apiStore.setRemote(originalRemote);
      this.apiService.configure(originalRemote, originalSecured);

      try {
        this.logger.trace('Waiting for reconnect after rollback', rollbackEndDeadline);
        await this.apiService.waitConnected(rollbackEndDeadline);
        this.logger.trace('Connection recovered');

        if (rollbackPostProcedure) {
          this.logger.trace('Calling rollback procedure callback');

          try {
            await rollbackPostProcedure();
          } catch (error) {
            this.logger.error(error.message, error);
          }
        }

      } catch (error) {
        this.logger.trace('Connection could not be recovered after rollback');
        throw new Error(`The connection could not be recovered<br>${error.message}`);
      }

      throw new Error(`The new settings could not be applied`);

    } finally {

      /* At this time the connection should be recovered, either the old or the new one */
      EventBus.Emit(EventBus.Event.EthernetConnectionUpdated);

      this.store.setSaveState(SaveState.Idle);

      this.logger.trace('Restoring API availability checks');
      this.apiStore.setCheckSuspended(false);
    }
  }

  private async CreateRestoreProcedure(networkingService: INetworkNetworkingService, timeout: number, devices?: string[]): Promise<NetworkRestoreProcedure> {

    const tickInterval = 0.1;

    const restoreProcedure = new NetworkRestoreProcedure(
      networkingService,
      timeout,
      tickInterval,
      devices,
      () => this.store.incrementTimer(tickInterval),
      false,
      false,
      true,
      true,
    );

    try {
      this.store.setTimer(0);
      this.store.setTimeout(timeout);
      await restoreProcedure.start();
    } catch (error) {
      error.message = `Could not create configuration restore point: ${error.message}`;
      throw error;
    }

    return restoreProcedure;
  }

  private async RemoveRestoreProcedure(restoreProcedure: NetworkRestoreProcedure) {
    try {
      await restoreProcedure.cancel();
    } catch (error) {
      error.message = `The restoration point could not be removed. A revert will occur, please wait...<br>${error.message}`;
      throw error;
    }
  }

}
